import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/i18n";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminGate } from "@/components/AdminGate";
import { AuthCallback } from "@/pages/AuthCallback";
import { Landing } from "@/pages/Landing";
import { Login } from "@/pages/Login";
import { ObdDecoder } from "@/pages/ObdDecoder";
import { ScanMobile } from "@/pages/ScanMobile";
import { ClientDashboard } from "@/pages/client/ClientDashboard";
import { NewOrder } from "@/pages/client/NewOrder";
import { ClientOrders } from "@/pages/client/ClientOrders";
import { OrderDetail } from "@/pages/client/OrderDetail";
import { BuyCredits } from "@/pages/client/BuyCredits";
import { Subscriptions } from "@/pages/client/Subscriptions";
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import { AdminOrders } from "@/pages/admin/AdminOrders";
import { AdminDefects } from "@/pages/admin/AdminDefects";
import { AdminOrderDetail } from "@/pages/admin/AdminOrderDetail";
import { AdminSolutions } from "@/pages/admin/AdminSolutions";
import { AdminAudit } from "@/pages/admin/AdminAudit";
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { AdminSettings } from "@/pages/admin/AdminSettings";
import { AdminPromo } from "@/pages/admin/AdminPromo";
import { ClientSettings } from "@/pages/client/ClientSettings";
import { Tickets } from "@/pages/Tickets";

function AppRouter() {
  const location = useLocation();
  React.useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("referral_code", ref.trim().toUpperCase());
  }, []);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/obd" element={<ObdDecoder />} />
      <Route path="/obd/:code" element={<ObdDecoder />} />
      <Route path="/scan/:token" element={<ScanMobile />} />

      <Route path="/client" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
      <Route path="/client/new" element={<ProtectedRoute><NewOrder /></ProtectedRoute>} />
      <Route path="/client/orders" element={<ProtectedRoute><ClientOrders /></ProtectedRoute>} />
      <Route path="/client/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
      <Route path="/client/credits" element={<ProtectedRoute><BuyCredits /></ProtectedRoute>} />
      <Route path="/client/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
      <Route path="/client/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
      <Route path="/client/settings" element={<ProtectedRoute><ClientSettings /></ProtectedRoute>} />

      <Route path="/admin" element={<AdminGate><AdminDashboard /></AdminGate>} />
      <Route path="/admin/orders" element={<AdminGate><AdminOrders /></AdminGate>} />
      <Route path="/admin/defects" element={<AdminGate><AdminDefects /></AdminGate>} />
      <Route path="/admin/orders/:id" element={<AdminGate><AdminOrderDetail /></AdminGate>} />
      <Route path="/admin/solutions" element={<AdminGate><AdminSolutions /></AdminGate>} />
      <Route path="/admin/audit" element={<AdminGate><AdminAudit /></AdminGate>} />
      <Route path="/admin/users" element={<AdminGate><AdminUsers /></AdminGate>} />
      <Route path="/admin/promo" element={<AdminGate><AdminPromo /></AdminGate>} />
      <Route path="/admin/settings" element={<AdminGate><AdminSettings section="payments" /></AdminGate>} />
      <Route path="/admin/settings/payments" element={<AdminGate><AdminSettings section="payments" /></AdminGate>} />
      <Route path="/admin/settings/catalog" element={<AdminGate><AdminSettings section="catalog" /></AdminGate>} />
      <Route path="/admin/settings/notifications" element={<AdminGate><AdminSettings section="notifications" /></AdminGate>} />
      <Route path="/admin/settings/services" element={<AdminGate><AdminSettings section="services" /></AdminGate>} />
      <Route path="/admin/settings/staff" element={<AdminGate><AdminSettings section="staff" /></AdminGate>} />
      <Route path="/admin/tickets" element={<AdminGate><Tickets /></AdminGate>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#111111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontFamily: "JetBrains Mono, monospace", fontSize: "12px" } }} />
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
